import { faClose, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import { bannerContentsServices } from '!core/redux';
import {
  TrlNewsTitle,
  TrlPill,
  TrlVideo,
  TrlVideoPill,
} from '»components/atoms';
import { useAlertModalStatus, useHandleSagaError } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { TrlFeedbackModal } from '../TrlFeedbackModal';
import {
  BodyStyled,
  BottomSection,
  CloseVideoIconStyled,
  ContainerStyled,
  ContentStyled,
  DateStyled,
  FileContainer,
  FileIconStyled,
  ImageStyled,
  MediaContainer,
  ScrollableContainerStyled,
  VideoBorder,
} from './TrlNewsContent.styles';

export const TrlNewsContent = ({
  title,
  image,
  bodyShort,
  date,
  contSeq,
  fileName,
  fileDescr,
  visibleFile,
  video,
  handleTitleClick,
  isInModal,
  isModalOpened,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [completedBody, setCompletedBody] = useState(null);
  const [currentDisplayedMedia, setCurrentDisplayedMedia] = useState(
    image ? 'image' : video ? 'video' : null,
  );

  const alertModalStatus = useAlertModalStatus(false);
  const { handleSagaError } = useHandleSagaError();

  const isShowingImage = currentDisplayedMedia === 'image';
  const isShowingVideo = currentDisplayedMedia === 'video';
  const hasFileToShow = fileName && visibleFile === 'Y';

  const fileUrlPattern = /^https?:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/.*)?$/;
  const isFileUrlValid = fileUrlPattern.test(fileName);

  useEffect(() => {
    if (isInModal && isModalOpened)
      bannerContentsServices
        .getContentBody({
          contSeq,
        })
        .then((response) => {
          setCompletedBody(response[0].body);
        })
        .catch((error) => {
          handleSagaError(error);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMediaContent = () => {
    if (isInModal) {
      if (isShowingImage)
        return (
          <MediaContainer isShowingVideo={false}>
            <TrlVideoPill
              handleOnClick={() => {
                setCurrentDisplayedMedia('video');
              }}
              hide={!video}
            />
            <ImageStyled component="img" src={image} isInModal={isInModal} />
          </MediaContainer>
        );

      if (isShowingVideo)
        return (
          <MediaContainer isShowingVideo>
            <VideoBorder position="top">
              <CloseVideoIconStyled
                icon={faClose}
                onClick={() => {
                  setCurrentDisplayedMedia('image');
                }}
                hide={!image}
              />
            </VideoBorder>
            <TrlVideo videoUrl={video} />
            <VideoBorder position="bottom" />
          </MediaContainer>
        );

      return null;
    }

    if (isShowingImage)
      return (
        <MediaContainer isShowingVideo={false}>
          <ImageStyled component="img" src={image} isInModal={isInModal} />
        </MediaContainer>
      );

    return null;
  };

  return (
    <ContainerStyled isInModal={isInModal}>
      <ScrollableContainerStyled isInModal={isInModal}>
        {getMediaContent()}
        <ContentStyled
          isInModal={isInModal}
          hasMedia={image || video}
          hasFileToShow={hasFileToShow}
        >
          {isInModal && <DateStyled isInModal={isInModal}>{date}</DateStyled>}
          <TrlNewsTitle
            title={title}
            maxLines={!isInModal && 2}
            overflowNotHidden={isInModal && isMobile}
            headlineNews
            handleClick={handleTitleClick}
          />
          <BottomSection isInModal={isInModal}>
            <BodyStyled
              dangerouslySetInnerHTML={{ __html: completedBody ?? bodyShort }}
              isInModal={isInModal}
            />
            {isInModal ? (
              isMobile &&
              hasFileToShow && (
                <FileContainer
                  href={fileName}
                  underline="none"
                  target="_blank"
                  rel="noopener"
                >
                  <FileIconStyled
                    icon={faFileLines}
                    className="fileLinesIcon"
                  />
                  {fileDescr}
                </FileContainer>
              )
            ) : (
              <DateStyled isInModal={isInModal}>{date}</DateStyled>
            )}
            <TrlPill
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-READ_ALL' })}
              variant="readAll"
              onClick={handleTitleClick}
            />
          </BottomSection>
        </ContentStyled>
      </ScrollableContainerStyled>
      {isInModal && !isMobile && hasFileToShow && (
        <>
          <FileContainer
            href={fileName}
            underline="none"
            target="_blank"
            rel="noopener"
            onClick={(event) => {
              if (!isFileUrlValid) {
                event.preventDefault();
                alertModalStatus.onChangeStatus({ isOpen: true });
              }
            }}
          >
            <FileIconStyled icon={faFileLines} className="fileLinesIcon" />
            {fileDescr}
          </FileContainer>
          <TrlFeedbackModal
            title={{ content: 'TRLT_UI-INVALID_FILE' }}
            descr={{ content: 'TRLT_UI-INVALID_FILE_DESC' }}
            type="warning"
            open={alertModalStatus.isOpen}
            onClose={() => {
              alertModalStatus.onChangeStatus({ isOpen: false });
            }}
          />
        </>
      )}
    </ContainerStyled>
  );
};

TrlNewsContent.propTypes = {
  title: PropTypes.string.isRequired,
  image: PropTypes.string,
  bodyShort: PropTypes.string,
  date: PropTypes.string.isRequired,
  contSeq: PropTypes.number,
  fileName: PropTypes.string,
  fileDescr: PropTypes.string,
  visibleFile: PropTypes.oneOf(['Y', 'N']),
  video: PropTypes.string,
  handleTitleClick: PropTypes.func,
  isInModal: PropTypes.bool,
  isModalOpened: PropTypes.bool,
};
